import React, { useMemo, useState, useEffect } from "react";
import { Badge, Avatar, Circlebar, Button, Card } from "components/ui";
import { toast } from "sonner";
import {
  PhoneIcon,
  ClockIcon,
  EyeIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  UserIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  XMarkIcon,
  XCircleIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  FlagIcon,
  FireIcon,
  PaperAirplaneIcon,
  PlusIcon,

} from "@heroicons/react/24/outline";
import {
  HeartIcon as HeartSolid,
  FlagIcon as FlagSolid,
} from "@heroicons/react/24/solid";
import {
  calculatePriority,
  getScoreLabel,
  getScoreProgressColor,
} from "../../../utils/priorityCalculator";
// import LeadTimelineDrawer from "./LeadTimelineDrawer";
import { Tooltip } from "../../shared/Tooltip";
import { hasPermission } from "../../../utils/permissionsUtils";
import { createLeadUser, getLeadDuplicates } from "../../../services/leadsService";
import { useLocation, Link } from "react-router";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { DuplicateLeadCard } from "../shared/DuplicateLeadCard";
import { counselorVerifyPhoneEmail } from "../../../services/leadsService";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  Dialog,
  Portal,
} from "@headlessui/react";
import { Fragment } from "react";
import clsx from "clsx";
import { formatDisplayDate } from "../../../utils/dateUtils";
import LeadTimeline from "./TimeLineView";
import EmailMessageModal from "../LeadActions/EmailMessageModal";

const LeadBasicInfo = ({
  leadData,
  isFavorite,
  isFlaged,
  selectedStage,
  selectedSubStage,
  isMobile,
  onEditStage,
  onEditPriority,
  onEditInteractionLevel,
  onRefreshLead,
  // Lead Management handlers
  onChangeStage,
  onSetPriority,
  onSetInteractionLevel,
  onOpenNotesModal,
  onEmailClick,
  entityName,
}) => {
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [isLoadingDuplicates, setIsLoadingDuplicates] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [isVerifying, setIsVerifying] = useState({ phone: false, email: false });
  const [showVerifyModal, setShowVerifyModal] = useState({ isOpen: false, type: null }); // type: 'phone' or 'email'
  const [showEmailModal, setShowEmailModal] = useState(false);

  const location = useLocation();
  const isManager = location.pathname.includes("manager");
  const duplicateAnchorId = useMemo(
    () => `lead-basic-duplicate-anchor-${leadData?.LeadId || leadData?.id}`,
    [leadData?.LeadId, leadData?.id],
  );
  const duplicateTooltipId = `${duplicateAnchorId}-tooltip`;

  const isPhoneVerified =
    leadData?.PhoneVerified === true

  const isEmailVerified =
    leadData?.EmailVerified === true

  const [showTimelineDrawer, setShowTimelineDrawer] = useState(false);
  const leadId = leadData?.LeadId || leadData?.id;

  // Enforce read-only status for Applicants and Applications
  const isReadOnly = leadData?.IsStudent;

  const isChangeStageRestricted = (selectedStage?.toLowerCase() === "closed" || selectedStage?.toLowerCase() === "close") && 
                                   (selectedSubStage?.toLowerCase() === "win" || selectedSubStage?.toLowerCase() === "registered");


  // Auto-load duplicates when lead has duplicates
  React.useEffect(() => {
    if (leadData?.HasDuplicate && duplicates.length === 0 && !isLoadingDuplicates) {
      loadDuplicates();
    }
  }, [leadData?.id, leadData?.HasDuplicate]);

  const handleToggleDuplicates = (event) => {
    event?.preventDefault?.();
    if (!leadData?.HasDuplicate) return;

    if (showDuplicates) {
      setShowDuplicates(false);
      return;
    }

    if (duplicates.length === 0 && !isLoadingDuplicates) {
      loadDuplicates();
    }

    setShowDuplicates(true);
  };

  React.useEffect(() => {
    if (!leadData?.HasDuplicate) {
      setShowDuplicates(false);
    }
  }, [leadData?.HasDuplicate]);

  const handleCreateUser = async () => {
    setIsCreatingUser(true);
    try {
      const result = await createLeadUser(leadData?.id);
      if (result.success) {
        toast.success(result.message || "User created successfully");
        if (onRefreshLead) onRefreshLead();
      } else {
        toast.error(result.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Failed to create user");
    } finally {
      setIsCreatingUser(false);
    }
  };
  const loadDuplicates = async () => {
    setIsLoadingDuplicates(true);
    try {
      const result = await getLeadDuplicates(leadData?.id);
      if (result.success) {
        setDuplicates(result.data || []);
      } else {
        console.error("Failed to fetch duplicates:", result.message);
      }
    } catch (error) {
      console.error("Error fetching duplicates:", error);
    } finally {
      setIsLoadingDuplicates(false);
    }
  };

  // Respect the prop if provided, otherwise determine from pathname
  const resolvedEntityName = entityName || (isManager ? "Lead" : "Applicant");

  const handleVerifyPhoneEmail = async (isPhone, close) => {
    const fieldType = isPhone ? "phone" : "email";
    const fieldValue = isPhone ? leadData?.mobile : leadData?.email;

    if (!fieldValue) {
      toast.error(`No ${fieldType} found to verify`);
      if (close) close();
      return;
    }

    setIsVerifying({ ...isVerifying, [fieldType]: true });

    try {
      const payload = {
        leadId: leadData?.id,
        fieldValue: fieldValue,
        isPhone: isPhone,
        isEmail: !isPhone
      };

      const result = await counselorVerifyPhoneEmail(payload);

      if (result.success) {
        toast.success(result.message || `${isPhone ? 'Phone' : 'Email'} verified successfully`);
        /** REFETCH LEAD DATA */
        if (onRefreshLead) onRefreshLead();
      } else {
        toast.error(result.message || `Failed to verify ${fieldType}`);
      }
    } catch (error) {
      console.error(`Error verifying ${fieldType}:`, error);
      toast.error(`Failed to verify ${fieldType}`);
    } finally {
      setIsVerifying({ ...isVerifying, [fieldType]: false });
      if (close) close();
    }
  };


  const getLeadScoreColor = (score) => {
    if (!score || score === "Unscored") return "neutral";
    const priority = calculatePriority(score);
    switch (priority?.id) {
      case "hot":
      case "urgent":
        return "error";
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "neutral";
    }
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase();
    if (p === 'high' || p === 'urgent' || p === 'hot') return 'error';
    if (p === 'medium') return 'warning';
    if (p === 'low') return 'success';
    return 'info';
  };

  // Add null check for leadData
  if (!leadData) {
    return null;
  }

  return (
    <>
      {/* Top Header Row: Avatar, Name, Badges, and Score (Horizontal like SS 1) */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            <Avatar
              name={leadData?.name || "Unknown"}
              size={16}
              initialColor="primary"
              className="rounded-full border-2 border-white dark:border-dark-800 shadow-sm"
            />
            {leadData?.IsInternationalLead && (
              <div className="absolute -bottom-0.5 -right-0.5 bg-white dark:bg-dark-800 rounded-full p-0.5 shadow-sm border border-gray-100 dark:border-dark-700">
                <Badge variant="soft" color="info" className="p-0.5"><EyeIcon className="w-2.5 h-2.5" /></Badge>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-lg font-black text-gray-900 dark:text-dark-50 tracking-tight truncate">
                  {leadData?.name}
                </h1>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 dark:text-dark-500 tracking-wide mb-1.5">
                <span>#{leadData.displayId || leadData.id}</span>
                <span className="opacity-30">|</span>
                <span className="truncate">{leadData.source || "Form - Quick Lead"}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge
                  variant="soft"
                  style={{ color: leadData?.InteractionLevelColorCode, borderColor: leadData?.InteractionLevelColorCode }}
                  className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-opacity-20 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    if (onEditInteractionLevel) onEditInteractionLevel();
                    else if (onSetInteractionLevel) onSetInteractionLevel();
                  }}
                >
                  {leadData?.InteractionLevelName}
                </Badge>
                <Badge
                  variant="soft"
                  color={getPriorityColor(leadData?.priority)}
                  className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-opacity-20 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    if (onEditPriority) onEditPriority();
                    else if (onSetPriority) onSetPriority();
                  }}
                >
                  {leadData?.priority || "High"}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Score Circle (SS 2 / Desktop Style) */}
        <div className="shrink-0 flex flex-col items-center ml-auto">
          <div className="relative">
            <Circlebar
              value={leadData.leadScore === "Unscored" ? 0 : leadData.leadScore || 0}
              size={14}
              strokeWidth={8}
              color={getLeadScoreColor(leadData.leadScore)}
            >
              <span className="text-lg font-black text-gray-900 dark:text-dark-50 leading-none">
                {leadData.leadScore === "Unscored" ? "--" : leadData.leadScore || 0}
              </span>
            </Circlebar>
          </div>
          <span className="text-[8px] font-black uppercase tracking-widest text-primary-600 dark:text-primary-400 mt-1.5">SCORE</span>
        </div>
      </div>

      {/* Second Row: Stage and Metadata Badges */}
      <div className="flex items-center gap-2 mb-2 overflow-x-auto hide-scrollbar pb-1 px-1">
        <div
          className={clsx(
            "flex items-center gap-1.5 px-2.5 py-1 bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400 rounded-lg border border-green-100 dark:border-green-900/30 transition-opacity",
            (isReadOnly || isChangeStageRestricted) ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:opacity-80"
          )}
          onClick={(!isReadOnly && !isChangeStageRestricted) ? (onEditStage || onChangeStage) : undefined}
        >
          <span className="text-[10px] font-black uppercase tracking-widest">{selectedStage}</span>
          <div className="w-[1px] h-2.5 bg-green-200 dark:bg-green-800 mx-1"></div>
          <span className="text-[10px] font-black uppercase tracking-widest">{selectedSubStage}</span>
        </div>
      </div>

      {/* Contact Information Cards (SS 2 Style) */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between p-2.5 rounded-2xl bg-gray-50/50 dark:bg-dark-800/30 border border-gray-100 dark:border-dark-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-blue-100/50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
              <PhoneIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-bold text-gray-900 dark:text-dark-50 truncate">{leadData.mobile || "N/A"}</span>
          </div>

          {leadData.mobile && leadData.mobile !== "N/A" && (
            <Popover className="relative flex shrink-0">
              <PopoverButton className="outline-none ml-2">
                <span
                  className={clsx(
                    "text-[9px] font-black px-2 py-1 rounded-full uppercase tracking-widest cursor-pointer transition-colors",
                    isPhoneVerified
                      ? "bg-secondary-50 text-secondary-600 hover:bg-secondary-100 dark:bg-secondary-900/20 dark:text-secondary-400"
                      : "bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400"
                  )}
                >
                  {isVerifying.phone ? 'Verifying...' : (isPhoneVerified ? 'Verified' : 'Not Verified')}
                </span>
              </PopoverButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <Portal>
                  <PopoverPanel
                    anchor={{ to: "bottom end", gap: 8 }}
                    className="z-[99999] w-[300px] rounded-xl border border-gray-200 bg-white p-4 shadow-xl outline-none ring-1 ring-black/5 dark:border-dark-700 dark:bg-dark-800 dark:shadow-none"
                  >
                    {({ close }) => (
                      <>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[12px] text-gray-700 dark:text-dark-200 font-medium leading-relaxed">
                              Are you sure you want to {isPhoneVerified ? 'unverify' : 'verify'} this phone number?
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex gap-2 justify-end">
                          <Button
                            onClick={() => close()}
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            className="h-8 text-[11px]"
                            disabled={isVerifying.phone}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => handleVerifyPhoneEmail(true, close)}
                            variant="filled"
                            color={isPhoneVerified ? "error" : "primary"}
                            size="sm"
                            className="h-8 text-[11px] inline-flex items-center gap-1.5"
                            disabled={isVerifying.phone}
                          >
                            {isVerifying.phone ? (
                              <>
                                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                {isPhoneVerified ? 'Unverifying...' : 'Verifying...'}
                              </>
                            ) : (
                              <>
                                <CheckBadgeIcon className="h-3.5 w-3.5" />
                                {isPhoneVerified ? 'Unverify' : 'Verify'}
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </PopoverPanel>
                </Portal>
              </Transition>
            </Popover>
          )}
        </div>

        <div className="flex items-center justify-between p-2.5 rounded-2xl bg-gray-50/50 dark:bg-dark-800/30 border border-gray-100 dark:border-dark-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-purple-100/50 dark:bg-purple-900/20 flex items-center justify-center shrink-0">
              <EnvelopeIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-sm font-bold text-gray-900 dark:text-dark-50 truncate">{leadData.email || "N/A"}</span>
          </div>

          {leadData.email && leadData.email !== "N/A" && (
            <Popover className="relative flex shrink-0">
              <PopoverButton className="outline-none ml-2">
                <span
                  className={clsx(
                    "text-[9px] font-black px-2 py-1 rounded-full uppercase tracking-widest cursor-pointer transition-colors",
                    isEmailVerified
                      ? "bg-secondary-50 text-secondary-600 hover:bg-secondary-100 dark:bg-secondary-900/20 dark:text-secondary-400"
                      : "bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400"
                  )}
                >
                  {isVerifying.email ? 'Verifying...' : (isEmailVerified ? 'Verified' : 'Not Verified')}
                </span>
              </PopoverButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <Portal>
                  <PopoverPanel
                    anchor={{ to: "bottom end", gap: 8 }}
                    className="z-[99999] w-[300px] rounded-xl border border-gray-200 bg-white p-4 shadow-xl outline-none ring-1 ring-black/5 dark:border-dark-700 dark:bg-dark-800 dark:shadow-none"
                  >
                    {({ close }) => (
                      <>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[12px] text-gray-700 dark:text-dark-200 font-medium leading-relaxed">
                              Are you sure you want to {isEmailVerified ? 'unverify' : 'verify'} this email address?
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex gap-2 justify-end">
                          <Button
                            onClick={() => close()}
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            className="h-8 text-[11px]"
                            disabled={isVerifying.email}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => handleVerifyPhoneEmail(false, close)}
                            variant="filled"
                            color={isEmailVerified ? "error" : "primary"}
                            size="sm"
                            className="h-8 text-[11px] inline-flex items-center gap-1.5"
                            disabled={isVerifying.email}
                          >
                            {isVerifying.email ? (
                              <>
                                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                {isEmailVerified ? 'Unverifying...' : 'Verifying...'}
                              </>
                            ) : (
                              <>
                                <CheckBadgeIcon className="h-3.5 w-3.5" />
                                {isEmailVerified ? 'Unverify' : 'Verify'}
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </PopoverPanel>
                </Portal>
              </Transition>
            </Popover>
          )}
        </div>

        {/* Timestamps */}
        <div className="grid grid-cols-1 gap-2 pt-4 border-t border-gray-100 dark:border-dark-800">
          <div className="flex items-center gap-2 text-[11px] font-bold text-gray-400 dark:text-dark-500 uppercase tracking-widest">
            <ClockIcon className="w-3.5 h-3.5" />
            <span>Added: {leadData.addedOn ? formatDisplayDate(leadData.addedOn) : "N/A"}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-bold text-gray-400 dark:text-dark-500 uppercase tracking-widest">
            <EyeIcon className="w-3.5 h-3.5" />
            <span>Last Active: {leadData?.lastActive ? formatDisplayDate(leadData.lastActive) : "N/A"}</span>
          </div>
        </div>
      </div>

      {showTimelineDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onClick={() => setShowTimelineDrawer(false)}>
          <div className="w-full max-w-lg bg-white dark:bg-dark-700 h-full shadow-xl">

            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold">Timeline</h2>
              <Button
                variant="flat"
                isIcon
                onClick={() => setShowTimelineDrawer(false)}
              >
                <XMarkIcon className="size-5" />
              </Button>
            </div>

            {/* Timeline */}
            <div className="h-[calc(100%-60px)] overflow-hidden p-4">
              <LeadTimeline
                leadId={leadId}
                isActive={showTimelineDrawer}
                entityName={resolvedEntityName}
              />
            </div>

          </div>
        </div>
      )}

      {leadData?.HasDuplicate && (
        <ReactTooltip
          id={duplicateTooltipId}
          anchorId={duplicateAnchorId}
          clickable
          openOnClick={true}
          openOnHover={false}
          place="bottom"
          offset={12}
          className="!z-[120] !rounded-xl !border !p-0 !shadow-xl [--rt-bg-color:#ffffff] [--rt-border-color:rgba(251,191,36,0.65)] dark:[--rt-bg-color:#0f172a] dark:[--rt-border-color:rgba(251,191,36,0.35)]"
          isOpen={showDuplicates}
          setIsOpen={(open) => {
            if (open && !showDuplicates) {
              setShowDuplicates(true);
            }
          }}
          closeOnEsc={false}
          closeOnScroll={false}
          keepTooltipInside
          positionStrategy="fixed"
          arrowColor="#f97316"
          style={{
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.15)",
            opacity: 1,
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
            "--rt-opacity": "1",
          }}
        >
          <div className="relative w-[min(340px,92vw)] sm:w-[min(520px,80vw)] md:w-[min(560px,72vw)] lg:w-[min(640px,65vw)] xl:w-[min(880px,55vw)] max-h-[70vh] lg:max-h-[80vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900">
            <div className="sticky top-0 z-20 mb-4 flex items-center justify-between gap-4 border-b border-amber-100/70 bg-white px-4 pb-3 pt-4 shadow-sm dark:border-amber-500/20 dark:bg-slate-900 sm:px-5">
              <div className="flex items-center gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Duplicate Leads
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Linked with #{leadData?.displayId || leadData?.id}
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/30 dark:text-amber-100">
                  {duplicates.length}
                </span>
              </div>
              <button
                onClick={() => setShowDuplicates(false)}
                className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-slate-800"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>

            {isLoadingDuplicates && duplicates.length === 0 ? (
              <div className="flex items-center justify-center gap-3 py-6 text-amber-700 dark:text-amber-200">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-amber-200 border-t-amber-500" />
                <span className="text-sm font-medium">Loading duplicates…</span>
              </div>
            ) : duplicates.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-600 dark:text-dark-300">
                No duplicate leads found.
              </p>
            ) : (
              <div className=" p-4 pt-0 grid gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                {duplicates.map((duplicate) => (
                  <DuplicateLeadCard
                    key={
                      duplicate?.LeadDuplicateId ||
                      `${duplicate?.DuplicateLeadId}-${duplicate?.MatchType}`
                    }
                    duplicate={duplicate}
                    isManager={isManager}
                    viewOnly
                  />
                ))}
              </div>
            )}
          </div>
        </ReactTooltip>
      )}

      <EmailMessageModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        leadData={leadData}
      />


    </>
  );
};

export default LeadBasicInfo;
